import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

interface PhoneVerificationProps {
  phone: string;
  onSuccess: () => void;
  onBack: () => void;
}

const PhoneVerification: React.FC<PhoneVerificationProps> = ({ phone, onSuccess, onBack }) => {
  const { verifyPhone, resendVerificationCode, loading } = useAuth();
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Timer for resend button
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }

    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setVerificationCode(value);
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit verification code');
      return;
    }

    const result = await verifyPhone(phone, verificationCode);
    
    if (result.success) {
      onSuccess();
    } else {
      setError(result.message);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    const result = await resendVerificationCode(phone);
    
    if (result.success) {
      setResendTimer(60);
      setCanResend(false);
      setError('');
      setVerificationCode('');
    } else {
      setError(result.message);
    }
  };

  const formatPhone = (phoneNumber: string) => {
    if (phoneNumber.startsWith('+27')) {
      return phoneNumber.replace('+27', '0');
    }
    return phoneNumber;
  };

  return (
    <div className="phone-verification">
      <div className="form-header">
        <div className="verification-icon">
          📱
        </div>
        <h1>Verify Your Phone</h1>
        <p>
          We've sent a 6-digit verification code to<br />
          <strong>{formatPhone(phone)}</strong>
        </p>
      </div>

      <form onSubmit={handleSubmit} className="verification-form">
        {error && (
          <div className="error-message general-error">
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="verificationCode">Verification Code</label>
          <input
            type="text"
            id="verificationCode"
            value={verificationCode}
            onChange={handleInputChange}
            placeholder="000000"
            className={`verification-input ${error ? 'error' : ''}`}
            maxLength={6}
            disabled={loading}
            autoFocus
          />
          <div className="input-help">
            Enter the 6-digit code sent to your phone
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-full"
          disabled={loading || verificationCode.length !== 6}
        >
          {loading ? (
            <span className="loading-spinner">
              <span className="dot"></span>
            </span>
          ) : (
            'Verify Phone Number'
          )}
        </button>

        <div className="verification-actions">
          <div className="resend-section">
            {canResend ? (
              <button
                type="button"
                className="link-button"
                onClick={handleResend}
                disabled={loading}
              >
                Resend verification code
              </button>
            ) : (
              <span className="resend-timer">
                Resend code in {resendTimer}s
              </span>
            )}
          </div>

          <button
            type="button"
            className="link-button"
            onClick={onBack}
            disabled={loading}
          >
            ← Back to login
          </button>
        </div>
      </form>

      <div className="verification-help">
        <h4>Didn't receive the code?</h4>
        <ul>
          <li>Check your SMS messages</li>
          <li>Make sure your phone has network coverage</li>
          <li>Wait a few minutes and try resending</li>
        </ul>
      </div>
    </div>
  );
};

export default PhoneVerification;


